import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Grid,
  Card,
  Badge,
  Button,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Group,
  Text,
  Table,
  Tabs,
  Alert,
  Switch,
  ActionIcon,
  LoadingOverlay,
  Progress,
} from "@mantine/core";
import {
  IconCategory,
  IconShield,
  IconCheck,
  IconEdit,
  IconTrash,
  IconAlertCircle,
  IconUserCheck,
  IconCoins,
  IconSettings,
} from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import {
  createPPRCategory,
  getPendingPPRApprovals,
  getPPRCategories,
  getUserCreditStatus,
  getUserExemptions,
  updatePPRCategory,
} from "../misc/api";
import { getGroup } from "../misc/tokens";

const CategoryCard = ({ category, onEdit, onDelete, userGroup }) => {
  const getPriorityColor = (level) => {
    switch (level) {
      case 3:
        return "red";
      case 2:
        return "orange";
      default:
        return "blue";
    }
  };

  const getPriorityText = (level) => {
    switch (level) {
      case 3:
        return "Emergency";
      case 2:
        return "Priority";
      default:
        return "Normal";
    }
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Group position="apart" mb="xs">
        <Group>
          <IconCategory size={20} />
          <Text weight={600}>{category.category_name}</Text>
          <Badge color={getPriorityColor(category.priority_level)} size="sm">
            {getPriorityText(category.priority_level)}
          </Badge>
        </Group>
        <Group>
          {category.is_free && (
            <Badge color="green" size="xs">
              Free
            </Badge>
          )}
          {category.requires_approval && (
            <Badge color="orange" size="xs">
              Approval Required
            </Badge>
          )}
        </Group>
      </Group>

      <Text size="xs" color="dimmed" mb="xs">
        Code: {category.category_code}
      </Text>

      <Text size="sm" mb="xs">
        Usage: {category.usage_count} PPRs
      </Text>

      {userGroup === "0" && (
        <Group mt="md" spacing="xs">
          <ActionIcon onClick={() => onEdit(category)} size="sm">
            <IconEdit size={14} />
          </ActionIcon>
          <ActionIcon
            color="red"
            onClick={() => onDelete(category.category_id)}
            size="sm"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      )}
    </Card>
  );
};

const CreateCategoryModal = ({
  opened,
  onClose,
  onSubmit,
  editingCategory,
}) => {
  const [form, setForm] = useState({
    category_code: "",
    category_name: "",
    description: "",
    is_free: false,
    requires_approval: false,
    approval_authority: "",
    priority_level: 1,
    auto_approve: false,
  });

  useEffect(() => {
    if (editingCategory) {
      setForm({
        category_code: editingCategory.category_code || "",
        category_name: editingCategory.category_name || "",
        description: editingCategory.description || "",
        is_free: editingCategory.is_free || false,
        requires_approval: editingCategory.requires_approval || false,
        approval_authority: editingCategory.approval_authority || "",
        priority_level: editingCategory.priority_level || 1,
        auto_approve: editingCategory.auto_approve || false,
      });
    } else {
      setForm({
        category_code: "",
        category_name: "",
        description: "",
        is_free: false,
        requires_approval: false,
        approval_authority: "",
        priority_level: 1,
        auto_approve: false,
      });
    }
  }, [editingCategory, opened]);

  const handleSubmit = () => {
    const submitData = editingCategory
      ? { ...form, category_id: editingCategory.category_id }
      : form;
    onSubmit(submitData);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editingCategory ? "Edit Category" : "Create New Category"}
      size="lg"
    >
      <Grid>
        <Grid.Col span={6}>
          <TextInput
            label="Category Code"
            placeholder="MILITARY, EMERGENCY, etc."
            value={form.category_code}
            onChange={(e) =>
              setForm({ ...form, category_code: e.target.value.toUpperCase() })
            }
            required
            mb="sm"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label="Priority Level"
            value={form.priority_level.toString()}
            onChange={(value) =>
              setForm({ ...form, priority_level: parseInt(value) })
            }
            data={[
              { value: "1", label: "Normal" },
              { value: "2", label: "Priority" },
              { value: "3", label: "Emergency" },
            ]}
            mb="sm"
          />
        </Grid.Col>
      </Grid>

      <TextInput
        label="Category Name"
        placeholder="Military Operations"
        value={form.category_name}
        onChange={(e) => setForm({ ...form, category_name: e.target.value })}
        required
        mb="sm"
      />

      <Textarea
        label="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        mb="sm"
      />

      <TextInput
        label="Approval Authority"
        placeholder="Who can approve these flights"
        value={form.approval_authority}
        onChange={(e) =>
          setForm({ ...form, approval_authority: e.target.value })
        }
        mb="sm"
      />

      <Group mb="md">
        <Switch
          label="Free PPR"
          checked={form.is_free}
          onChange={(e) =>
            setForm({ ...form, is_free: e.currentTarget.checked })
          }
        />
        <Switch
          label="Requires Approval"
          checked={form.requires_approval}
          onChange={(e) =>
            setForm({ ...form, requires_approval: e.currentTarget.checked })
          }
        />
        <Switch
          label="Auto Approve"
          checked={form.auto_approve}
          onChange={(e) =>
            setForm({ ...form, auto_approve: e.currentTarget.checked })
          }
        />
      </Group>

      <Group position="right">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!form.category_code || !form.category_name}
        >
          {editingCategory ? "Update Category" : "Create Category"}
        </Button>
      </Group>
    </Modal>
  );
};

const CreateExemptionModal = ({ opened, onClose, onSubmit, categories }) => {
  const [form, setForm] = useState({
    user_email: "",
    category_id: "",
    exemption_type: "permanent",
    valid_until: "",
    conditions: "",
    documentation_reference: "",
    notes: "",
  });

  const handleSubmit = () => {
    onSubmit(form);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Create User Exemption"
      size="md"
    >
      <TextInput
        label="User Email"
        placeholder="user@example.com"
        value={form.user_email}
        onChange={(e) => setForm({ ...form, user_email: e.target.value })}
        required
        mb="sm"
      />

      <Select
        label="Category"
        placeholder="Select category"
        value={form.category_id}
        onChange={(value) => setForm({ ...form, category_id: value })}
        data={categories.map((cat) => ({
          value: cat.category_id.toString(),
          label: cat.category_name,
        }))}
        required
        mb="sm"
      />

      <Select
        label="Exemption Type"
        value={form.exemption_type}
        onChange={(value) => setForm({ ...form, exemption_type: value })}
        data={[
          { value: "permanent", label: "Permanent" },
          { value: "temporary", label: "Temporary" },
          { value: "conditional", label: "Conditional" },
        ]}
        mb="sm"
      />

      {form.exemption_type !== "permanent" && (
        <DateInput
          label="Valid Until"
          value={form.valid_until ? new Date(form.valid_until) : null}
          onChange={(date) =>
            setForm({ ...form, valid_until: date?.toISOString().split("T")[0] })
          }
          mb="sm"
        />
      )}

      <Textarea
        label="Conditions"
        placeholder="Special conditions for this exemption"
        value={form.conditions}
        onChange={(e) => setForm({ ...form, conditions: e.target.value })}
        mb="sm"
      />

      <TextInput
        label="Documentation Reference"
        placeholder="Official document reference"
        value={form.documentation_reference}
        onChange={(e) =>
          setForm({ ...form, documentation_reference: e.target.value })
        }
        mb="sm"
      />

      <Textarea
        label="Notes"
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        mb="md"
      />

      <Group position="right">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!form.user_email || !form.category_id}
        >
          Create Exemption
        </Button>
      </Group>
    </Modal>
  );
};

const ApprovalWorkflow = ({ pendingApprovals, onApprove, onReject }) => {
  const [selectedPPR, setSelectedPPR] = useState(null);
  const [approvalModal, setApprovalModal] = useState(false);
  const [action, setAction] = useState("");
  const [notes, setNotes] = useState("");

  const handleAction = (ppr, actionType) => {
    setSelectedPPR(ppr);
    setAction(actionType);
    setApprovalModal(true);
  };

  const handleSubmit = () => {
    if (action === "approve") {
      onApprove(selectedPPR.ppr_number, notes);
    } else {
      onReject(selectedPPR.ppr_number, notes);
    }
    setApprovalModal(false);
    setNotes("");
  };

  const getPriorityBadge = (level) => {
    const colors = { 3: "red", 2: "orange", 1: "blue" };
    const labels = { 3: "Emergency", 2: "Priority", 1: "Normal" };
    return (
      <Badge color={colors[level]} size="sm">
        {labels[level]}
      </Badge>
    );
  };

  return (
    <>
      <Card withBorder>
        <Text weight={600} mb="md">
          PPRs Requiring Approval ({pendingApprovals.length})
        </Text>

        {pendingApprovals.length === 0 ? (
          <Text color="dimmed" ta="center" py="xl">
            No PPRs pending approval
          </Text>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>PPR</th>
                <th>Category</th>
                <th>User</th>
                <th>Route</th>
                <th>Priority</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingApprovals.map((ppr) => (
                <tr key={ppr.ppr_number}>
                  <td>{ppr.ppr_number}</td>
                  <td>{ppr.category_name}</td>
                  <td>{ppr.user_name || "Unknown"}</td>
                  <td>
                    {ppr.departure_place} → {ppr.arrival_place}
                  </td>
                  <td>{getPriorityBadge(ppr.priority_level)}</td>
                  <td>{new Date(ppr.created_at).toLocaleDateString()}</td>
                  <td>
                    <Group spacing="xs">
                      <Button
                        size="xs"
                        color="green"
                        onClick={() => handleAction(ppr, "approve")}
                      >
                        Approve
                      </Button>
                      <Button
                        size="xs"
                        color="red"
                        variant="outline"
                        onClick={() => handleAction(ppr, "reject")}
                      >
                        Reject
                      </Button>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        opened={approvalModal}
        onClose={() => setApprovalModal(false)}
        title={`${action === "approve" ? "Approve" : "Reject"} PPR ${
          selectedPPR?.ppr_number
        }`}
      >
        <Alert icon={<IconAlertCircle size="1rem" />} mb="md">
          <Text size="sm">
            <strong>PPR:</strong> {selectedPPR?.ppr_number}
            <br />
            <strong>Category:</strong> {selectedPPR?.category_name}
            <br />
            <strong>User:</strong> {selectedPPR?.user_name}
            <br />
            <strong>Route:</strong> {selectedPPR?.departure_place} →{" "}
            {selectedPPR?.arrival_place}
          </Text>
        </Alert>

        <Textarea
          label={action === "approve" ? "Approval Notes" : "Rejection Reason"}
          placeholder={
            action === "approve"
              ? "Additional notes..."
              : "Reason for rejection..."
          }
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          required={action === "reject"}
          mb="md"
        />

        <Group position="right">
          <Button variant="outline" onClick={() => setApprovalModal(false)}>
            Cancel
          </Button>
          <Button
            color={action === "approve" ? "green" : "red"}
            onClick={handleSubmit}
            disabled={action === "reject" && !notes}
          >
            {action === "approve" ? "Approve PPR" : "Reject PPR"}
          </Button>
        </Group>
      </Modal>
    </>
  );
};

const CreditManagement = ({ creditStatus, creditReservations }) => {
  const totalCredits = creditStatus.reduce(
    (sum, pkg) => sum + (pkg.credits_available || 0),
    0
  );
  const usedCredits = creditStatus.reduce(
    (sum, pkg) => sum + (pkg.credits_used || 0),
    0
  );
  const reservedCredits = creditStatus.reduce(
    (sum, pkg) => sum + (pkg.credits_reserved || 0),
    0
  );

  return (
    <Grid>
      <Grid.Col span={12}>
        <Card withBorder>
          <Text weight={600} mb="md">
            Credit Overview
          </Text>

          <Group mb="md">
            <div>
              <Text size="xl" weight={700} color="green">
                {totalCredits}
              </Text>
              <Text size="xs" color="dimmed">
                Available
              </Text>
            </div>
            <div>
              <Text size="xl" weight={700} color="blue">
                {usedCredits}
              </Text>
              <Text size="xs" color="dimmed">
                Used
              </Text>
            </div>
            <div>
              <Text size="xl" weight={700} color="orange">
                {reservedCredits}
              </Text>
              <Text size="xs" color="dimmed">
                Reserved
              </Text>
            </div>
          </Group>

          {creditStatus.map((pkg) => (
            <Card key={pkg.transaction_id} withBorder mb="sm">
              <Group position="apart" mb="xs">
                <Text weight={500}>{pkg.package_name}</Text>
                <Badge color={pkg.credits_available > 0 ? "green" : "gray"}>
                  {pkg.credits_available}/{pkg.credit_amount} Credits
                </Badge>
              </Group>

              <Progress
                value={(pkg.credits_used / pkg.credit_amount) * 100}
                sections={[
                  {
                    value: (pkg.credits_used / pkg.credit_amount) * 100,
                    color: "blue",
                  },
                  {
                    value: (pkg.credits_reserved / pkg.credit_amount) * 100,
                    color: "orange",
                  },
                ]}
                size="sm"
                mb="xs"
              />

              <Group spacing="lg">
                <Text size="xs" color="dimmed">
                  Used: {pkg.credits_used}
                </Text>
                <Text size="xs" color="dimmed">
                  Reserved: {pkg.credits_reserved}
                </Text>
                <Text size="xs" color="dimmed">
                  Available: {pkg.credits_available}
                </Text>
              </Group>
            </Card>
          ))}
        </Card>
      </Grid.Col>
    </Grid>
  );
};

const PPRCategoriesSystem = () => {
  const [activeTab, setActiveTab] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [userExemptions, setUserExemptions] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [creditStatus, setCreditStatus] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [createCategoryModal, setCreateCategoryModal] = useState(false);
  const [createExemptionModal, setCreateExemptionModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  // Get user group from your auth context/localStorage
  const userGroup = getGroup();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const categoriesData = await getPPRCategories();
      setCategories(categoriesData.data || []);

      const exemptionsData = await getUserExemptions();
      setUserExemptions(exemptionsData.data || []);

      if (userGroup === "0" || userGroup === "100") {
        const approvalsData = await getPendingPPRApprovals();
        setPendingApprovals(approvalsData.data || []);
      }

      if (userGroup === "1") {
        const creditsData = await getUserCreditStatus();
        setCreditStatus(creditsData.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, [userGroup]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateCategory = async (categoryData) => {
    try {
      if (categoryData.category_id) {
        updatePPRCategory(categoryData);
      } else {
        createPPRCategory(categoryData);
      }
      setCreateCategoryModal(false);
      await loadData();
    } catch (error) {
      console.error("Error creating category:", error);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCreateCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await loadData();
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const handleCreateExemption = async (exemptionData) => {
    try {
      setCreateExemptionModal(false);
      await loadData();
    } catch (error) {
      console.error("Error creating exemption:", error);
    }
  };

  const handleApprovePPR = async (pprNumber, notes) => {
    try {
      await loadData();
    } catch (error) {
      console.error("Error approving PPR:", error);
    }
  };

  const handleRejectPPR = async (pprNumber, reason) => {
    try {
      await loadData();
    } catch (error) {
      console.error("Error rejecting PPR:", error);
    }
  };

  const getTabsForUser = () => {
    const tabs = [
      {
        value: "categories",
        label: "Categories",
        icon: <IconCategory size={14} />,
      },
    ];

    if (userGroup === "1") {
      tabs.push(
        {
          value: "my-exemptions",
          label: "My Exemptions",
          icon: <IconShield size={14} />,
        },
        {
          value: "credits",
          label: "Credit Management",
          icon: <IconCoins size={14} />,
        }
      );
    }

    if (userGroup === "0" || userGroup === "100") {
      tabs.push(
        // {
        //   value: "approvals",
        //   label: "Approvals",
        //   icon: <IconCheck size={14} />,
        // },
        {
          value: "exemptions",
          label: "Manage Exemptions",
          icon: <IconUserCheck size={14} />,
        }
      );
    }

    return tabs;
  };

  return (
    <Container size="xl" py="md">
      <Group position="apart" mb="lg">
        <Text size="xl" weight={700}>
          <IconCategory size={28} style={{ marginRight: 8 }} />
          PPR Categories & Management
        </Text>

        <Group>
          {userGroup === "0" && (
            <>
              <Button
                leftIcon={<IconSettings size={16} />}
                onClick={() => {
                  setEditingCategory(null);
                  setCreateCategoryModal(true);
                }}
              >
                Add Category
              </Button>
              <Button
                leftIcon={<IconUserCheck size={16} />}
                onClick={() => setCreateExemptionModal(true)}
              >
                Add Exemption
              </Button>
            </>
          )}
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          {getTabsForUser().map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} icon={tab.icon}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        <Tabs.Panel value="categories" pt="md">
          <LoadingOverlay visible={loading} />
          <Grid>
            {categories.map((category) => (
              <Grid.Col key={category.category_id} xs={12} sm={6} md={4}>
                <CategoryCard
                  category={category}
                  onEdit={handleEditCategory}
                  onDelete={handleDeleteCategory}
                  userGroup={userGroup}
                />
              </Grid.Col>
            ))}
          </Grid>

          {categories.length === 0 && !loading && (
            <Text color="dimmed" ta="center" py="xl">
              No categories found.
            </Text>
          )}
        </Tabs.Panel>

        {userGroup === "1" && (
          <Tabs.Panel value="my-exemptions" pt="md">
            <Card withBorder>
              <Text weight={600} mb="md">
                My PPR Exemptions
              </Text>
              {userExemptions.length === 0 ? (
                <Text color="dimmed" ta="center" py="xl">
                  You have no PPR exemptions.
                </Text>
              ) : (
                <Table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Issued By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userExemptions.map((exemption) => (
                      <tr key={exemption.exemption_id}>
                        <td>{exemption.category_name}</td>
                        <td>{exemption.exemption_type}</td>
                        <td>
                          <Badge
                            color={
                              exemption.exemption_status === "Active"
                                ? "green"
                                : "gray"
                            }
                          >
                            {exemption.exemption_status}
                          </Badge>
                        </td>
                        <td>{exemption.issued_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          </Tabs.Panel>
        )}

        {userGroup === "1" && (
          <Tabs.Panel value="credits" pt="md">
            <CreditManagement
              creditStatus={creditStatus}
              creditReservations={[]}
            />
          </Tabs.Panel>
        )}

        {(userGroup === "0" || userGroup === "100") && (
          <Tabs.Panel value="approvals" pt="md">
            <ApprovalWorkflow
              pendingApprovals={pendingApprovals}
              onApprove={handleApprovePPR}
              onReject={handleRejectPPR}
            />
          </Tabs.Panel>
        )}

        {userGroup === "0" && (
          <Tabs.Panel value="exemptions" pt="md">
            <Card withBorder>
              <Group position="apart" mb="md">
                <Text weight={600}>Manage User Exemptions</Text>
                <Button
                  leftIcon={<IconUserCheck size={16} />}
                  onClick={() => setCreateExemptionModal(true)}
                >
                  Add Exemption
                </Button>
              </Group>

              <Alert icon={<IconAlertCircle size="1rem" />}>
                <Text size="sm">
                  User exemptions allow specific users to bypass PPR fees for
                  certain categories. Use this feature for military personnel,
                  emergency services, and other authorized users.
                </Text>
              </Alert>
            </Card>
          </Tabs.Panel>
        )}
      </Tabs>

      <CreateCategoryModal
        opened={createCategoryModal}
        onClose={() => {
          setCreateCategoryModal(false);
          setEditingCategory(null);
        }}
        onSubmit={handleCreateCategory}
        editingCategory={editingCategory}
      />

      <CreateExemptionModal
        opened={createExemptionModal}
        onClose={() => setCreateExemptionModal(false)}
        onSubmit={handleCreateExemption}
        categories={categories}
      />
    </Container>
  );
};

export default PPRCategoriesSystem;
